import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  Divider,
  Avatar,
  Paper,
  useTheme,
  Alert,
  Snackbar,
  Fab,
  Tooltip,
  Stack
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Notes as NotesIcon,
  Group as GroupIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon,
  ContactPhone as ContactPhoneIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { makeApiCall } from '../../config/api';
import CustomerForm from './CustomerForm';

const CustomerDetailView = ({ customerId, onBack }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user } = useAuth();

  // State
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Edit form state
  const [showEditForm, setShowEditForm] = useState(false);
  const [lookups, setLookups] = useState({
    lead_sources: [],
    property_types: [],
    customer_groups: [],
    sales_persons: []
  });

  // Note dialog state
  const [openNoteDialog, setOpenNoteDialog] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteForm, setNoteForm] = useState({ subject: '', content: '' });

  // Fetch customer details
  const fetchCustomer = async () => {
    if (!customerId) return;
    
    try {
      setLoading(true);
      const response = await makeApiCall(`api/customers/${customerId}`);

      if (!response.ok) {
        throw new Error('Customer not found');
      }

      const data = await response.json();
      setCustomer(data);
    } catch (error) {
      console.error('Error fetching customer:', error);
      setError('Failed to load customer details');
    } finally {
      setLoading(false);
    }
  };

  // Fetch lookups for edit form
  const fetchLookups = async () => {
    try {
      const response = await makeApiCall('api/customers/lookups/all');
      if (response.ok) {
        const data = await response.json();
        setLookups(data);
      }
    } catch (error) {
      console.error('Error fetching lookups:', error);
    }
  };

  // Add/Edit note
  const handleSaveNote = async () => {
    try {
      const endpoint = editingNote 
        ? `api/customers/${customerId}/notes/${editingNote.id}`
        : `api/customers/${customerId}/notes`;
      
      const method = editingNote ? 'PUT' : 'POST';

      const response = await makeApiCall(endpoint, {
        method,
        body: JSON.stringify(noteForm)
      });

      if (!response.ok) {
        throw new Error('Failed to save note');
      }

      setSuccess(editingNote ? 'Note updated successfully' : 'Note added successfully');
      setOpenNoteDialog(false);
      setEditingNote(null);
      setNoteForm({ subject: '', content: '' });
      fetchCustomer(); // Refresh customer data
    } catch (error) {
      console.error('Error saving note:', error);
      setError('Failed to save note');
    }
  };

  // Delete note
  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      const response = await makeApiCall(`api/customers/${customerId}/notes/${noteId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete note');
      }

      setSuccess('Note deleted successfully');
      fetchCustomer(); // Refresh customer data
    } catch (error) {
      console.error('Error deleting note:', error);
      setError('Failed to delete note');
    }
  };

  // Open note dialog
  const openAddNoteDialog = () => {
    setEditingNote(null);
    setNoteForm({ subject: '', content: '' });
    setOpenNoteDialog(true);
  };

  const openEditNoteDialog = (note) => {
    setEditingNote(note);
    setNoteForm({ subject: note.subject, content: note.content });
    setOpenNoteDialog(true);
  };

  // Handle customer edit
  const handleEditCustomer = () => {
    setShowEditForm(true);
  };

  const handleEditSuccess = (message) => {
    setSuccess(message);
    setShowEditForm(false);
    fetchCustomer(); // Refresh customer data
  };

  const handleEditCancel = () => {
    setShowEditForm(false);
  };

  // Get customer type color and icon
  const getCustomerTypeData = (type) => {
    switch (type) {
      case 'residential': 
        return { color: 'primary', icon: <PersonIcon />, label: t('quotes.residential') };
      case 'commercial': 
        return { color: 'secondary', icon: <BusinessIcon />, label: t('quotes.commercial') };
      case 'construction': 
        return { color: 'warning', icon: <TrendingUpIcon />, label: t('quotes.construction') };
      default: 
        return { color: 'default', icon: <PersonIcon />, label: t('crm.unknown') };
    }
  };

  // Get status color
  const getStatusColor = (customer) => {
    if (!customer?.is_active) return 'error';
    if (customer?.is_signed_client) return 'success';
    if (customer?.quoted) return 'info';
    return 'default';
  };

  // Get status text
  const getStatusText = (customer) => {
    if (!customer?.is_active) return t('crm.inactive');
    if (customer?.is_signed_client) return t('crm.signedClient');
    if (customer?.quoted) return t('crm.quoted');
    return t('common.active');
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    fetchCustomer();
    fetchLookups();
  }, [customerId]);

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" mb={4}>
          <IconButton onClick={onBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">Loading...</Typography>
        </Box>
      </Box>
    );
  }

  if (error && !customer) {
    return (
      <Box sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" mb={4}>
          <IconButton onClick={onBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">Customer Details</Typography>
        </Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const customerTypeData = getCustomerTypeData(customer?.customer_type);

  // Show edit form if editing
  if (showEditForm) {
    return (
      <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
        <Box display="flex" alignItems="center" mb={4}>
          <IconButton 
            onClick={handleEditCancel} 
            sx={{ 
              mr: 2, 
              bgcolor: 'grey.100', 
              '&:hover': { bgcolor: 'grey.200' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Edit Customer
          </Typography>
        </Box>
        
        <CustomerForm
          customer={customer}
          onSuccess={handleEditSuccess}
          onCancel={handleEditCancel}
          lookups={lookups}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }} data-demo-target="page-customer-detail-root">
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center">
          <IconButton 
            onClick={onBack} 
            data-demo-target="page-customer-detail-back"
            sx={{ 
              mr: 2, 
              bgcolor: 'grey.100', 
              '&:hover': { bgcolor: 'grey.200' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
              {customer?.full_name || `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim()}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip
                icon={customerTypeData.icon}
                label={customerTypeData.label}
                color={customerTypeData.color}
                variant="filled"
              />
              <Chip
                label={getStatusText(customer)}
                color={getStatusColor(customer)}
                variant="outlined"
              />
            </Stack>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={handleEditCustomer}
          sx={{ 
            bgcolor: theme.palette.primary.main,
            px: 3,
            py: 1.5
          }}
        >
          Edit Customer
        </Button>
      </Box>

      <Grid container spacing={4}>
        {/* Left Column - Customer Information */}
        <Grid size={12}>
          <Stack spacing={3}>
            {/* Customer Overview Card */}
            <Card elevation={2}>
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" mb={3}>
                  <Avatar 
                    sx={{ 
                      mr: 3, 
                      bgcolor: theme.palette.primary.main, 
                      width: 64, 
                      height: 64,
                      fontSize: '1.5rem'
                    }}
                  >
                    {customer?.company_name ? <BusinessIcon fontSize="large" /> : <PersonIcon fontSize="large" />}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                      {customer?.full_name || `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim()}
                    </Typography>
                    {customer?.company_name && (
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        {customer.company_name}
                      </Typography>
                    )}
                    {customer?.job_title && (
                      <Typography variant="body2" color="text.secondary">
                        {customer.job_title}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Contact Information Card */}
            <Card elevation={2}>
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" mb={3}>
                  <ContactPhoneIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Contact Information
                  </Typography>
                </Box>
                
                <Grid container spacing={3}>
                  {customer?.email_address && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box display="flex" alignItems="center" p={2} bgcolor="grey.50" borderRadius={2}>
                        <EmailIcon sx={{ mr: 2, color: 'primary.main' }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Email Address
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {customer.email_address}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  
                  {customer?.phone_number && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box display="flex" alignItems="center" p={2} bgcolor="grey.50" borderRadius={2}>
                        <PhoneIcon sx={{ mr: 2, color: 'primary.main' }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Phone Number
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {customer.phone_number}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  
                  {customer?.mobile_number && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box display="flex" alignItems="center" p={2} bgcolor="grey.50" borderRadius={2}>
                        <PhoneIcon sx={{ mr: 2, color: 'primary.main' }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Mobile Number
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {customer.mobile_number}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  
                  {customer?.email_address_2 && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box display="flex" alignItems="center" p={2} bgcolor="grey.50" borderRadius={2}>
                        <EmailIcon sx={{ mr: 2, color: 'primary.main' }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Secondary Email
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {customer.email_address_2}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Address Card */}
            {(customer?.street_address || customer?.city) && (
              <Card elevation={2}>
                <CardContent sx={{ p: 4 }}>
                  <Box display="flex" alignItems="center" mb={3}>
                    <LocationIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Address
                    </Typography>
                  </Box>
                  
                  <Box p={2} bgcolor="grey.50" borderRadius={2}>
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      {customer?.street_address && (
                        <>
                          {customer.street_address}<br />
                        </>
                      )}
                      {customer?.city && customer?.province && (
                        <>
                          {customer.city}, {customer.province}
                          {customer?.postal_code && <> {customer.postal_code}</>}
                        </>
                      )}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Business Details Card */}
            <Card elevation={2}>
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" mb={3}>
                  <GroupIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Business Details
                  </Typography>
                </Box>
                
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box p={2} bgcolor="grey.50" borderRadius={2}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Sales Person
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {customer?.sales_person_name || t('tasks.unassigned')}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box p={2} bgcolor="grey.50" borderRadius={2}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Lead Source
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {customer?.lead_source_name || t('crm.unknown')}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box p={2} bgcolor="grey.50" borderRadius={2}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Property Type
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {customer?.property_type_name || 'Not specified'}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box p={2} bgcolor="grey.50" borderRadius={2}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Customer Group
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {customer?.customer_group_name || t('crm.none')}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box p={2} bgcolor="grey.50" borderRadius={2}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Created
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatDate(customer?.created_at)}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  {customer?.updated_at && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box p={2} bgcolor="grey.50" borderRadius={2}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Last Updated
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {formatDate(customer?.updated_at)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Notes Section */}
            <Card elevation={2}>
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Box display="flex" alignItems="center">
                    <NotesIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Notes ({customer?.notes?.length || 0})
                    </Typography>
                  </Box>
                  <Tooltip title={t('tasks.addNote')}>
                    <IconButton 
                      color="primary" 
                      onClick={openAddNoteDialog}
                      sx={{ 
                        bgcolor: 'primary.light', 
                        color: 'white',
                        '&:hover': { bgcolor: 'primary.main' }
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                {customer?.notes?.length === 0 ? (
                  <Box textAlign="center" py={6}>
                    <NotesIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      No notes yet
                    </Typography>
                    <Typography variant="body2" color="textSecondary" mb={3}>
                      Add the first note to start tracking customer interactions.
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={openAddNoteDialog}
                    >
                      Add First Note
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
                    <Stack spacing={2}>
                      {customer?.notes?.map((note) => (
                        <Paper key={note.id} elevation={1} sx={{ p: 3, borderLeft: 4, borderColor: 'primary.main' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1, textAlign: 'left' }}>
                              {note.subject}
                            </Typography>
                            <Box display="flex" gap={0.5}>
                              <IconButton
                                size="small"
                                onClick={() => openEditNoteDialog(note)}
                                sx={{ color: 'primary.main' }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteNote(note.id)}
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                          
                          <Typography variant="body2" color="textPrimary" paragraph sx={{ textAlign: 'left' }}>
                            {note.content}
                          </Typography>
                          
                          <Box display="flex" alignItems="center" gap={1}>
                            <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="textSecondary">
                              {formatDate(note.created_at)} by {note.created_by}
                            </Typography>
                          </Box>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {/* Add/Edit Note Dialog */}
      <Dialog 
        open={openNoteDialog} 
        onClose={() => setOpenNoteDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {editingNote ? t('tasks.editNote') : t('tasks.addNote')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={3}>
            <TextField
              autoFocus
              label={t('tasks.subject')}
              fullWidth
              variant="outlined"
              value={noteForm.subject}
              onChange={(e) => setNoteForm({ ...noteForm, subject: e.target.value })}
              placeholder="Enter a brief subject for this note"
            />
            <TextField
              label={t('tasks.content')}
              fullWidth
              multiline
              rows={6}
              variant="outlined"
              value={noteForm.content}
              onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
              placeholder="Enter the detailed note content here..."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={() => setOpenNoteDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleSaveNote}
            variant="contained"
            disabled={!noteForm.subject.trim() || !noteForm.content.trim()}
          >
            {editingNote ? t('common.edit') : t('tasks.addNote')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
      >
        <Alert onClose={() => setSuccess('')} severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert onClose={() => setError('')} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerDetailView; 